import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  FormControl,
  IconButton,
  ListItemIcon,
  Menu,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import Loader from "layout/Loader";
import { CheckRTL } from "Helpers/CheckRTL";
import { truncDynCell } from "common/Table/truncDynCell";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import InvoiceDownloadIcon from "assets/download-invoice";
import { selectUserInfo } from "features/user/userSlice";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
interface Column {
  id: string;
  label: string;
  numeric: boolean;
}

interface ExpensesTableProps {
  columns: Column[];
  data: any[];
  showHeader: boolean;
  showSearch: boolean;
  showFilter: boolean;
  tableData?: any;
  headerComponent?: React.ReactNode;
  handleChangePage: (field: number) => void;
  handleChangeRows: (field: number) => void;
  pageNo: number;
  pageSize: number;
  rowClick?: boolean;
  showRowView?: (row: any) => void;
  loadingSearch?: boolean;
  showActions: boolean;
  actions?: {
    label: string;
    onClick: (value: any) => void;
    icon?: React.ReactNode;
  }[];
}

const ExpensesTable: React.FC<ExpensesTableProps> = ({
  columns,
  data,
  showHeader,
  showSearch,
  headerComponent,
  handleChangePage,
  handleChangeRows,
  tableData,
  pageSize,
  rowClick,
  showRowView,
  loadingSearch,
  actions,
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);
  const [selectedRow, setSelectedRow] = useState({});
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t, i18n } = useTranslation();
  const isRTL = CheckRTL();
  const handleClickmenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleChangePage1 = (action: string) => {
    if (tableData) {
      if (action === "first") {
        if (tableData?.pageable?.pageNumber > 0) {
          handleChangePage(0);
        }
      } else if (action === "prev") {
        if (tableData.pageable && tableData?.pageable?.pageNumber > 0) {
          handleChangePage(tableData?.pageable?.pageNumber - 1);
        }
      } else if (action === "next") {
        if (!tableData?.last) {
          handleChangePage(tableData?.pageable?.pageNumber + 1);
        }
      } else if (action === "last") {
        if (!tableData?.last) {
          handleChangePage(tableData?.totalPages - 1);
        }
      }
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangeRows(parseInt(event.target.value));
    setPage(0);
  };

  const visibleRows = useMemo(() => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [data, page, rowsPerPage]);
  return (
    <Box className="w-[100%] ">
      <Paper
        className="w-[100%] mb-[2]"
        sx={{ boxShadow: "none", background: themeColors?.appBg }}
      >
        {showHeader && showSearch ? (
          <Box
            className="table-header-toolbar"
            sx={{
              minHeight: "56px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              width: "100%",
              margin: "5px 0px",
              gap: 1,
              "& > .MuiGrid-container, & > .table-header-search": {
                flex: "1 1 360px",
                width: "auto !important",
                maxWidth: "100%",
                minWidth: 0,
                alignSelf: "flex-start",
              },
              "& > .MuiGrid-item": {
                flex: "0 0 auto",
                width: "auto !important",
                maxWidth: "none",
                marginInlineStart: "auto",
                alignSelf: "flex-start",
              },
              "& > .MuiGrid-item ~ .MuiGrid-item": {
                marginInlineStart: 0,
              },
              "& > .MuiGrid-item ~ *:not(.MuiGrid-container):not(.MuiGrid-item):not(.table-header-search)":
                {
                  flex: "0 0 auto",
                },
              "& > .table-header-actions": {
                flex: "0 0 auto",
                marginInlineStart: "auto",
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "flex-start",
                alignSelf: "flex-start",
                justifyContent: "flex-end",
                gap: 1,
                "& .MuiButton-root": { marginInlineStart: 0 },
              },
            }}
          >
            {headerComponent}
          </Box>
        ) : null}
        {loadingSearch ? (
          <Loader />
        ) : (
          <>
            <TableContainer
              sx={{
                borderRadius: "8px",
                // marginTop: "10px",
                overflow: "visible",
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{ backgroundColor: "#fff" }}
                  className="sticky top-[42px] z-[10]"
                >
                  <TableRow>
                    {columns.map((column, columnIndex) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          textAlign: isRTL
                            ? "right !important"
                            : "left !important",
                        }}
                        padding={column.id === "actions" ? "normal" : "normal"}
                        sortDirection={false}
                        className={`font-Saira font-[400] text-[13px] ${columnIndex !== columns.length - 1 ? "borderRightApplied" : ""} ${column.id === "entityNo" ? "w-[150px]" : ""}`}
                      >
                        {
                          <span className="font-semibold">
                            {t(column.label)}
                          </span>
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        align="center"
                        className="py-3"
                      >
                        <Typography
                          className="font-Saira font-[500] text-[15px] italic"
                          sx={{ color: themeColors?.primary }}
                        >
                          {t("noRecordFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object?.entries(
                      visibleRows.reduce(
                        (acc: Record<string, typeof data>, row) => {
                          const date = new Date(row?.date);
                          const today = new Date();
                          const yesterday = new Date();
                          yesterday.setDate(today.getDate() - 1);
                          const formattedDate =
                            date.toDateString() === today.toDateString()
                              ? t("today")
                              : date.toDateString() === yesterday.toDateString()
                                ? t("yesterday")
                                : new Date(row?.date).toLocaleDateString(
                                    "en-GB",
                                  );
                          if (!acc[formattedDate]) acc[formattedDate] = [];
                          acc[formattedDate].push(row);
                          return acc;
                        },
                        {},
                      ),
                    )?.map(([date, rows]) => (
                      <React.Fragment key={date}>
                        {/* <Typography className={`font-Saira font-[700] text-[14px] text-start ${index !== 0 ? 'mt-5' : ''}`} sx={{ color: themeColors?.primary }}>
                                    {date}
                                </Typography> */}
                        {rows.map((row, index) => (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={false}
                            tabIndex={-1}
                            key={index}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? themeColors?.appBg : "#fff",
                              cursor: "pointer",
                            }}
                          >
                            {columns.map((column, columnIndex) => (
                              <TableCell
                                onClick={() => {
                                  rowClick && column.id !== "actions"
                                    ? showRowView?.(row)
                                    : "";
                                }}
                                className={`font-Saira font-[400] text-[13px] ${columnIndex !== columns.length - 1 ? "borderRightApplied" : ""} ${isRTL ? "text-right" : "text-left"} ${column.id === "entityNo" ? "w-[150px]" : ""}`}
                                component={
                                  column.id === "url" ? "div" : undefined
                                }
                                padding={"normal"}
                                align={"left"}
                              >
                                {column.id === "actions" ? (
                                  <Box>
                                    {
                                      <IconButton
                                        aria-label="more"
                                        id="long-button"
                                        aria-controls={
                                          open ? "long-menu" : undefined
                                        }
                                        aria-expanded={
                                          open ? "true" : undefined
                                        }
                                        aria-haspopup="true"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleClickmenu(event, row);
                                        }}
                                      >
                                        <Tooltip
                                          enterDelay={0}
                                          title={t("moreOptions")}
                                        >
                                          <MoreHorizIcon />
                                        </Tooltip>
                                      </IconButton>
                                    }
                                    <Menu
                                      slotProps={{
                                        root: { sx: { zIndex: 0 } },
                                      }}
                                      id="long-menu"
                                      disableScrollLock
                                      MenuListProps={{
                                        "aria-labelledby": "long-button",
                                      }}
                                      anchorEl={anchorEl}
                                      open={open}
                                      onClose={handleClose}
                                      PaperProps={{
                                        elevation: 0,
                                        sx: {
                                          overflow: "visible",
                                          filter:
                                            "drop-shadow(0px 2px 2px rgba(0,0,0,0.05))",
                                          // mt: 1.5,
                                          "& .MuiAvatar-root": {
                                            width: 32,
                                            height: 32,
                                            // ml: -0.5,
                                            // mr: 1,
                                          },
                                          "&::before": {
                                            content: '""',
                                            display: "block",
                                            position: "absolute",
                                            top: 0,
                                            left: 14,
                                            width: 10,
                                            height: 10,
                                            bgcolor: "background.paper",
                                            transform:
                                              "translateY(-50%) rotate(45deg)",
                                            zIndex: 0,
                                          },
                                        },
                                      }}
                                    >
                                      {actions?.map((option) => (
                                        <div onClick={handleClose}>
                                          <MenuItem
                                            className="font-Saira font-[400] text-[14]"
                                            key={option.label}
                                            onClick={() =>
                                              option.onClick(selectedRow)
                                            }
                                          >
                                            <ListItemIcon>
                                              {option.icon}
                                            </ListItemIcon>
                                            {t(option.label)}
                                          </MenuItem>
                                        </div>
                                      ))}
                                    </Menu>
                                  </Box>
                                ) : column.id === "amount" ? (
                                  new Intl.NumberFormat(
                                    "en-US",
                                    loggedInUserCurrency
                                      ? {
                                          style: "currency",
                                          currency: loggedInUserCurrency || "",
                                        }
                                      : {},
                                  ).format(row?.amount)
                                ) : column.id === "date" ? (
                                  new Intl.DateTimeFormat(i18n.language, {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    numberingSystem:
                                      i18n.language === "ur" ||
                                      i18n.language === "ar"
                                        ? "arab"
                                        : "latn",
                                  }).format(new Date(row?.date))
                                ) : column.id === "url" ? (
                                  Array.isArray(row?.url) ? (
                                    row.url.map((u: string, i: number) => (
                                      <Tooltip
                                        key={i}
                                        title={u.split("/").pop() || ""}
                                      >
                                        <Box
                                          onClick={(e: any) => {
                                            e.stopPropagation();
                                            window.open(u, "_blank");
                                          }}
                                          className="cursor-pointer w-[18px]"
                                          sx={{ "& svg": { width: "18px" } }}
                                        >
                                          <InvoiceDownloadIcon
                                            primary={themeColors?.primary}
                                            secondary={themeColors?.secondary}
                                          />
                                        </Box>
                                      </Tooltip>
                                    ))
                                  ) : row?.url ? (
                                    <Tooltip
                                      title={row.url.split("/").pop() || ""}
                                    >
                                      <Box
                                        onClick={(e: any) => {
                                          e.stopPropagation();
                                          window.open(row.url, "_blank");
                                        }}
                                        className="cursor-pointer w-[18px]"
                                        sx={{ "& svg": { width: "18px" } }}
                                      >
                                        <InvoiceDownloadIcon
                                          primary={themeColors?.primary}
                                          secondary={themeColors?.secondary}
                                        />
                                      </Box>
                                    </Tooltip>
                                  ) : null
                                ) : (
                                  truncDynCell(t(row[column.id]))
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <div
              style={{
                padding: "0 20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span className="hidden-xs mr-2 font-Saira font-[400] text-[13px]">
                {t("rowsPerPage")}
              </span>
              <span style={{ marginRight: 10, minWidth: 60 }}>
                {/* <strong>{tableData && tableData.pageable.pageSize}</strong>{" "} */}

                <FormControl className="m-1 min-w-[70]" size="small">
                  <Select
                    className="font-Saira font-[500] text-[13px] h-[25px] mt-[2px]"
                    value={
                      (tableData && tableData?.pageable?.pageSize) || pageSize
                    }
                    // value={rowsPerPageOptions[2]}
                    onChange={(event) =>
                      handleChangeRowsPerPage(
                        event as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                    // style={{height:"30px"}}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <MenuItem
                        className="font-Saira font-[500] text-[13px]"
                        key={option}
                        value={option}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </span>
              <span style={{ flex: 1 }}></span>

              <span
                // style={{ marginRight: 5, whiteSpace: "nowrap", maxWidth: 300 }}
                className="mr-2 whitespace-nowrap max-w-[300] font-Saira font-[400] text-[13px]"
              >
                {t("page")}
                <strong className="font-Saira ml-2 font-[700] text-[14px]">
                  {tableData && tableData.pageable
                    ? tableData.pageable.pageNumber + 1
                    : 1}
                </strong>{" "}
                <span className="visible-responsive"></span>
              </span>
              <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                {t("outOf")}

                <strong className="ml-2 font-Saira font-[700] text-[14px]">
                  {tableData && tableData.totalPages ? tableData.totalPages : 1}
                </strong>
              </span>
              <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                {t("totalRecords")}
                {/* )}{" "}  */}
                <strong className="ml-2 font-Saira font-[700] text-[14px]">
                  {tableData && tableData.totalElements
                    ? tableData.totalElements
                    : 0}
                </strong>
              </span>
              <span className="mr-2">
                <FirstPageIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("first")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <NavigateBeforeIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("prev")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.first
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <NavigateNextIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("next")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
              <span className="mr-2">
                <LastPageIcon
                  fontSize="small"
                  onClick={() => handleChangePage1("last")}
                  style={{
                    color:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "#1F2020"
                        : "#B8C0C6",
                    cursor:
                      tableData?.content?.length &&
                      tableData?.content?.length !== 0 &&
                      !tableData.last
                        ? "pointer"
                        : "text",
                  }}
                />
              </span>
            </div>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ExpensesTable;
const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100];
